import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
type Page = 'home' | 'about';
interface NavbarProps {
  currentPage: Page;
  onNavigate: (page: Page) => void;
}
export function Navbar({
  currentPage,
  onNavigate
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleNavClick = (page: Page, sectionId?: string) => {
    onNavigate(page);
    setIsMobileMenuOpen(false);
    if (sectionId) {
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const navBackground = isScrolled ? 'bg-white/90 backdrop-blur-xl border-b border-[#c9a961]/20 shadow-sm' : 'bg-transparent border-transparent';
  const textColor = isScrolled ? 'text-[#1d1d1f]' : 'text-white';
  const linkColor = isScrolled ? 'text-[#86868b] hover:text-[#c9a961]' : 'text-white/80 hover:text-white';
  const activeLinkColor = isScrolled ? 'text-[#1d1d1f]' : 'text-white';
  return <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBackground}`} data-id="element-134">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between" data-id="element-135">
          {/* Logo */}
          <button onClick={() => handleNavClick('home')} className={`font-display text-[24px] font-bold tracking-tight transition-colors duration-300 flex items-center gap-2 ${textColor}`} data-id="element-136">
            <span className="text-[#c9a961] text-3xl" data-id="element-137">BV</span>
            <span className="tracking-widest uppercase text-sm font-sans mt-1" data-id="element-138">
              Group
            </span>
          </button>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-10" data-id="element-139">
            <button onClick={() => handleNavClick('home')} className={`text-[14px] uppercase tracking-widest font-medium transition-colors duration-300 ${currentPage === 'home' ? activeLinkColor : linkColor}`} data-id="element-140">
              Home
            </button>
            <button onClick={() => handleNavClick('home', 'businesses')} className={`text-[14px] uppercase tracking-widest font-medium transition-colors duration-300 ${linkColor}`} data-id="element-141">
              Our Businesses
            </button>
            <button onClick={() => handleNavClick('about')} className={`text-[14px] uppercase tracking-widest font-medium transition-colors duration-300 ${currentPage === 'about' ? activeLinkColor : linkColor}`} data-id="element-142">
              About
            </button>
            <button onClick={() => handleNavClick('home', 'contact')} className={`text-[14px] uppercase tracking-widest font-medium transition-colors duration-300 ${linkColor}`} data-id="element-143">
              Contact
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button className={`md:hidden p-2 -mr-2 transition-colors duration-300 ${textColor}`} onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu" data-id="element-144">
            <MenuIcon className="w-6 h-6" data-id="element-145" />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-146">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-[60] bg-[#1d1d1f] flex flex-col" data-id="element-147">
            <div className="flex items-center justify-between px-6 h-20 border-b border-white/10" data-id="element-148">
              <span className="font-display text-[24px] font-bold text-white tracking-tight flex items-center gap-2" data-id="element-149">
                <span className="text-[#c9a961] text-3xl" data-id="element-150">BV</span>
                <span className="tracking-widest uppercase text-sm font-sans mt-1" data-id="element-151">
                  Group
                </span>
              </span>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 -mr-2 text-white/80 hover:text-white transition-colors" aria-label="Close menu" data-id="element-152">
                <XIcon className="w-6 h-6" data-id="element-153" />
              </button>
            </div>

            <div className="flex-1 flex flex-col justify-center px-8 gap-8" data-id="element-154">
              <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} onClick={() => handleNavClick('home')} className={`font-display text-4xl font-medium text-left ${currentPage === 'home' ? 'text-[#c9a961]' : 'text-white'}`} data-id="element-155">
                Home
              </motion.button>
              <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.15
          }} onClick={() => handleNavClick('home', 'businesses')} className="font-display text-4xl font-medium text-left text-white/80 hover:text-white" data-id="element-156">
                Our Businesses
              </motion.button>
              <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} onClick={() => handleNavClick('about')} className={`font-display text-4xl font-medium text-left ${currentPage === 'about' ? 'text-[#c9a961]' : 'text-white'}`} data-id="element-157">
                About
              </motion.button>
              <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} onClick={() => handleNavClick('home', 'contact')} className="font-display text-4xl font-medium text-left text-white/80 hover:text-white" data-id="element-158">
                Contact
              </motion.button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}